/* SSi website – Iaith ar Daith Celebrity Coaching page.
 *
 * Editorial intro (Jeremy Vine → S4C origin) + a magazine-style
 * grid of all 18 celebrity coaching videos. Click a tile and the
 * poster swaps inline for an HTML5 <video> player (no modal).
 *
 * Video sources are served from media.<env>.saysomethingin.com.
 * `VITE_MEDIA_BASE` is set per-environment by the deploy workflows.
 */
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const MEDIA_BASE = import.meta.env.VITE_MEDIA_BASE || '';

/* ---------- Data ---------- */

// Source-of-truth list. `series` is internal grouping only — we don't
// surface it on the page (per design choice). Order is roughly newest-first
// within each series, but presented as one continuous grid.
export const COACHING = [
  // Series 4 – 2023
  { slug: 'joe-ledley',      name: 'Joe Ledley',       role: 'Footballer',                                src: `${MEDIA_BASE}/celebrities/joe-ledley.mp4` },
  { slug: 'jessica-hynes',   name: 'Jessica Hynes',    role: 'Actor & writer',                            src: `${MEDIA_BASE}/celebrities/jessica-hynes.mp4` },
  { slug: 'aleighcia-scott', name: 'Aleighcia Scott',  role: 'Singer-songwriter',                         src: `${MEDIA_BASE}/celebrities/aleighcia-scott.mp4` },
  { slug: 'neet-mohan',      name: 'Neet Mohan',       role: 'Actor (Casualty)',                          src: `${MEDIA_BASE}/celebrities/neet-mohan.mp4` },
  { slug: 'jayde-adams',     name: 'Jayde Adams',      role: 'Comedian & actor',                          src: `${MEDIA_BASE}/celebrities/jayde-adams.mp4` },
  // Series 3 – 2022
  { slug: 'katie-owen',      name: 'Katie Owen',       role: 'Actor',                                     src: `${MEDIA_BASE}/celebrities/katie-owen.mp4` },
  { slug: 'mike-bubbins',    name: 'Mike Bubbins',     role: 'Comedian (Mammoth)',                        src: `${MEDIA_BASE}/celebrities/mike-bubbins.mp4` },
  { slug: 'amy-dowden',      name: 'Amy Dowden',       role: 'Strictly Come Dancing pro',                 src: `${MEDIA_BASE}/celebrities/amy-dowden.mp4` },
  // Series 2 – 2021
  { slug: 'steve-backshall', name: 'Steve Backshall',  role: 'Explorer & presenter',                      src: `${MEDIA_BASE}/celebrities/steve-backshall.mp4` },
  { slug: 'rakie-ayola',     name: 'Rakie Ayola',      role: 'Actor',                                     src: `${MEDIA_BASE}/celebrities/rakie-ayola.mp4` },
  { slug: 'james-hook',      name: 'James Hook',       role: 'Wales rugby international',                 src: `${MEDIA_BASE}/celebrities/james-hook.mp4` },
  { slug: 'chris-coleman',   name: 'Chris Coleman',    role: 'Football manager',                          src: `${MEDIA_BASE}/celebrities/chris-coleman.mp4` },
  { slug: 'joanna-scanlan',  name: 'Joanna Scanlan',   role: 'Actor',                                     src: `${MEDIA_BASE}/celebrities/joanna-scanlan.mp4` },
  // Series 1 – 2020
  { slug: 'carol-vorderman', name: 'Carol Vorderman',  role: 'TV host & educator',                        src: `${MEDIA_BASE}/celebrities/carol-vorderman.mp4` },
  { slug: 'colin-jackson',   name: 'Colin Jackson',    role: 'Olympic hurdler',                           src: `${MEDIA_BASE}/celebrities/colin-jackson.mp4` },
  { slug: 'ruth-jones',      name: 'Ruth Jones',       role: 'Actor & writer (Gavin & Stacey)',           src: `${MEDIA_BASE}/celebrities/ruth-jones.mp4` },
  { slug: 'adrian-chiles',   name: 'Adrian Chiles',    role: 'Broadcaster',                               src: `${MEDIA_BASE}/celebrities/adrian-chiles.mp4` },
  { slug: 'scott-quinnell',  name: 'Scott Quinnell',   role: 'Rugby legend',                              src: `${MEDIA_BASE}/celebrities/scott-quinnell.mp4` },
];

// Soft, on-brand gradient backgrounds for fallback posters — cycles through
// these so adjacent tiles don't collide. Inspired by the brand palette.
const FALLBACK_BGS = [
  'linear-gradient(135deg, #7B0F1A 0%, #B2192D 100%)',
  'linear-gradient(135deg, #1a4d3c 0%, #2a6b56 100%)',
  'linear-gradient(135deg, #B8762A 0%, #D89A4F 100%)',
  'linear-gradient(135deg, #2d4a6e 0%, #466688 100%)',
  'linear-gradient(135deg, #4d2a52 0%, #6b3d72 100%)',
  'linear-gradient(135deg, #5a3a1f 0%, #7d5530 100%)',
];

function initials(name) {
  return name.split(/[\s-]+/).slice(0, 2).map(s => s[0] || '').join('').toUpperCase();
}

/* ---------- Hero ---------- */

export function CCHero({ t }) {
  return (
    <section className="ssi-cc__hero" style={ccHeroStyles.wrap}>
      <div style={ccHeroStyles.inner}>
        <div style={ccHeroStyles.kicker}>{t.ccKicker}</div>
        <h1 style={ccHeroStyles.h1}>{t.ccTitle}</h1>
        <p style={ccHeroStyles.lede}>{t.ccLede}</p>
        <div style={ccHeroStyles.metaRow}>
          <div style={ccHeroStyles.metaCell}>
            <div style={ccHeroStyles.metaNum}>4</div>
            <div style={ccHeroStyles.metaLabel}>series on S4C</div>
          </div>
          <div style={ccHeroStyles.metaDivider} aria-hidden="true"/>
          <div style={ccHeroStyles.metaCell}>
            <div style={ccHeroStyles.metaNum}>18</div>
            <div style={ccHeroStyles.metaLabel}>celebrity learners</div>
          </div>
          <div style={ccHeroStyles.metaDivider} aria-hidden="true"/>
          <div style={ccHeroStyles.metaCell}>
            <div style={ccHeroStyles.metaNum}>2020 –</div>
            <div style={ccHeroStyles.metaLabel}>and counting</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Featured: the original Jeremy Vine session ----------
 *
 * Jeremy Vine is the genesis of the whole Iaith ar Daith story but he was
 * never part of the S4C series, so he isn't one of the 18 portrait tiles
 * below. This block features the full Radio 2 session from 2019, in a
 * landscape player to set it apart from the portrait grid.
 *
 * Two source files (HD ~1.2 GB, SD ~75 MB). The visitor picks which to
 * stream — no surprise bandwidth burn, and no clever-but-seamy adaptive
 * swap. The chosen <video> mounts only on click. If the file isn't
 * uploaded yet, onError surfaces a graceful "coming soon" state instead
 * of a broken player.
 */
const FEATURE_SRC_HD = `${MEDIA_BASE}/jeremy-vine-1080p.mp4`;
const FEATURE_SRC_SD = `${MEDIA_BASE}/jeremy-vine-720p.mp4`;

export function CCFeature({ t }) {
  const [chosen, setChosen] = useState(null); // 'hd' | 'sd' | null
  const [failed, setFailed] = useState(false);
  const src = chosen === 'hd' ? FEATURE_SRC_HD : chosen === 'sd' ? FEATURE_SRC_SD : null;

  const pick = (q) => { setFailed(false); setChosen(q); };

  return (
    <section className="ssi-cc__feature" style={ccFeatStyles.wrap}>
      <div style={ccFeatStyles.inner}>
        <div className="ssi-cc__feature-grid" style={ccFeatStyles.grid}>
          <div style={ccFeatStyles.playerCol}>
            <div style={ccFeatStyles.frame}>
              <span style={ccFeatStyles.originTag}>{t.ccFeatTag}</span>

              {!chosen && (
                <div style={ccFeatStyles.posterWrap}>
                  <div style={ccFeatStyles.posterBg} aria-hidden="true"/>
                  <div style={ccFeatStyles.posterStripes} aria-hidden="true"/>
                  <div style={ccFeatStyles.posterScrim} aria-hidden="true"/>
                  <div style={ccFeatStyles.posterMeta}>
                    <div style={ccFeatStyles.posterName}>Jeremy Vine</div>
                    <div style={ccFeatStyles.posterRole}>BBC Radio 2 · 2019</div>
                  </div>
                  <div style={ccFeatStyles.qualityCentre}>
                    <div style={ccFeatStyles.qualityRow}>
                      <button
                        type="button"
                        onClick={() => pick('hd')}
                        style={ccFeatStyles.qualityBtnPrimary}
                        aria-label="Play in 1080p, 1.2 GB"
                      >
                        <svg width="18" height="18" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path d="M10 7v14l11-7-11-7z" fill="#fff"/></svg>
                        <span>1080p · 1.2 GB</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => pick('sd')}
                        style={ccFeatStyles.qualityBtnGhost}
                        aria-label="Play in 720p, 75 MB"
                      >
                        <svg width="18" height="18" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path d="M10 7v14l11-7-11-7z" fill="#fff"/></svg>
                        <span>720p · 75 MB</span>
                      </button>
                    </div>
                    <div style={ccFeatStyles.qualityHint}>Pick the quality that suits your connection.</div>
                  </div>
                </div>
              )}

              {chosen && !failed && (
                <video
                  key={chosen}
                  src={src}
                  controls
                  autoPlay
                  preload="auto"
                  playsInline
                  style={ccFeatStyles.video}
                  onError={() => setFailed(true)}
                />
              )}

              {chosen && failed && (
                <div style={ccFeatStyles.errorState}>
                  <div style={ccFeatStyles.errorTitle}>Video coming soon</div>
                  <div style={ccFeatStyles.errorBody}>The original session is being uploaded.</div>
                </div>
              )}
            </div>
          </div>

          <div style={ccFeatStyles.textCol}>
            <div style={ccFeatStyles.kicker}>{t.ccFeatKicker}</div>
            <h2 style={ccFeatStyles.h2}>{t.ccFeatTitle}</h2>
            <p style={ccFeatStyles.body}>{t.ccFeatBody}</p>
            <div style={ccFeatStyles.chips}>
              <span style={ccFeatStyles.chip}>{t.ccFeatChip1}</span>
              <span style={ccFeatStyles.chip}>{t.ccFeatChip2}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Single video card ---------- */

function CoachingCard({ person, index, isPlaying, onPlay, onClose }) {
  // We render the <video> itself as the poster (paused, muted, preload=metadata).
  // The browser shows the first frame natively — no CORS-tainted canvas needed.
  // If the video fails to load metadata, fall back to a branded gradient + initials.
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef(null);
  const fallbackBg = FALLBACK_BGS[index % FALLBACK_BGS.length];

  // Seek slightly into the video on metadata load so browsers reliably show
  // a non-black first frame as the poster.
  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    if (!isPlaying && v.duration && v.duration > 1.5) {
      try { v.currentTime = 1.2; } catch (_) {}
    }
  };

  // When the user hits play, jump to t=0 + start it.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isPlaying) {
      v.controls = true;
      v.muted = false;
      try { v.currentTime = 0; } catch (_) {}
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      v.controls = false;
      v.muted = true;
      try { v.pause(); } catch (_) {}
    }
  }, [isPlaying]);

  return (
    <article style={ccCardStyles.card}>
      <div style={ccCardStyles.posterWrap}>
        {/* The <video> doubles as the poster when paused. */}
        {!videoFailed && (
          <video
            ref={videoRef}
            src={`${person.src}#t=1.2`}
            preload="metadata"
            playsInline
            muted
            style={ccCardStyles.video}
            onLoadedMetadata={onLoadedMetadata}
            onEnded={onClose}
            onError={() => setVideoFailed(true)}
          />
        )}

        {/* Fallback if video fails to load */}
        {videoFailed && (
          <div style={{ ...ccCardStyles.posterFallback, background: fallbackBg }} aria-hidden="true">
            <span style={ccCardStyles.posterInitials}>{initials(person.name)}</span>
          </div>
        )}

        {/* Idle overlay (poster mode): scrim + name/role + play button */}
        {!isPlaying && (
          <button
            type="button"
            onClick={onPlay}
            style={ccCardStyles.posterBtn}
            aria-label={`Play ${person.name}'s coaching video`}
          >
            <div style={ccCardStyles.posterScrim} aria-hidden="true"/>
            <div style={ccCardStyles.overlay}>
              <div style={ccCardStyles.overlayName}>{person.name}</div>
              <div style={ccCardStyles.overlayRole}>{person.role}</div>
            </div>
            <span style={ccCardStyles.play} aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 28 28" fill="none">
                <path d="M10 7v14l11-7-11-7z" fill="#fff"/>
              </svg>
            </span>
          </button>
        )}

        {/* Close button (only while playing) */}
        {isPlaying && (
          <button
            type="button"
            onClick={onClose}
            style={ccCardStyles.closeBtn}
            aria-label="Close video"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        )}
      </div>
    </article>
  );
}

/* ---------- Grid ---------- */

export function CCGrid({ t }) {
  const [playing, setPlaying] = useState(null); // slug or null
  return (
    <section className="ssi-cc__grid" style={ccGridStyles.wrap}>
      <div style={ccGridStyles.inner}>
        <div style={ccGridStyles.head}>
          <h2 style={ccGridStyles.h2}>{t.ccGridTitle}</h2>
          <p style={ccGridStyles.sub}>{t.ccGridSub}</p>
        </div>
        <div className="ssi-cc__row" style={ccGridStyles.row}>
          {COACHING.map((p, i) => (
            <CoachingCard
              key={p.slug}
              person={p}
              index={i}
              isPlaying={playing === p.slug}
              onPlay={() => setPlaying(p.slug)}
              onClose={() => setPlaying(null)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Outro ---------- */

export function CCOutro({ t }) {
  return (
    <section className="ssi-cc__outro" style={ccOutroStyles.wrap}>
      <div style={ccOutroStyles.inner}>
        <h2 style={ccOutroStyles.h2}>{t.ccOutroTitle}</h2>
        <p style={ccOutroStyles.body}>{t.ccOutroBody}</p>
        <div style={ccOutroStyles.ctaRow}>
          <Link to="/#sample" style={ccOutroStyles.ctaPrimary}>{t.ccOutroCta1}</Link>
          {/* <Link to="/gift" style={ccOutroStyles.ctaGhost}>{t.ccOutroCta2}</Link> -- commented out while gift voucher pricing/setup is being decided */}
        </div>
      </div>
    </section>
  );
}

/* ---------- Styles ---------- */

const ccHeroStyles = {
  wrap: { background:'#fff', padding:'88px 32px 56px' },
  inner: { maxWidth:880, margin:'0 auto', textAlign:'center' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:600, marginBottom:18 },
  h1: { fontFamily:'var(--font-display)', fontSize:60, lineHeight:1.04, letterSpacing:'-.02em', margin:'0 0 24px', textWrap:'balance' },
  lede: { fontSize:19, lineHeight:1.6, color:'var(--grey)', margin:'0 auto 40px', maxWidth:720, textWrap:'pretty' },
  metaRow: { display:'inline-flex', alignItems:'center', gap:0, padding:'4px 0', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)' },
  metaCell: { padding:'14px 28px', display:'flex', flexDirection:'column', alignItems:'center', gap:4 },
  metaNum: { fontFamily:'var(--font-display)', fontSize:28, lineHeight:1, letterSpacing:'-.01em', color:'var(--fg-1)' },
  metaLabel: { fontSize:11.5, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--grey)', fontWeight:500 },
  metaDivider: { width:1, height:32, background:'var(--border)' },
};

const ccGridStyles = {
  wrap: { background:'var(--offWhite, #FDF5F5)', padding:'72px 32px 96px' },
  inner: { maxWidth:1320, margin:'0 auto' },
  head: { maxWidth:680, margin:'0 0 48px', textAlign:'left' },
  h2: { fontFamily:'var(--font-display)', fontSize:36, lineHeight:1.1, letterSpacing:'-.015em', margin:'0 0 12px' },
  sub: { fontSize:16, lineHeight:1.55, color:'var(--grey)', margin:0, textWrap:'pretty', maxWidth:560 },
  row: {
    display:'grid',
    gridTemplateColumns:'repeat(3, minmax(0, 1fr))',
    gap:24,
  },
};

const ccCardStyles = {
  card: { display:'flex', flexDirection:'column' },
  posterWrap: {
    position:'relative',
    aspectRatio:'3/4',
    borderRadius:14,
    overflow:'hidden',
    background:'#1a0a0a',
    boxShadow:'0 8px 24px -10px rgba(73,3,0,.25), 0 2px 6px -2px rgba(73,3,0,.10)',
  },
  posterBtn: {
    position:'absolute', inset:0,
    width:'100%', height:'100%',
    border:'none', padding:0,
    background:'transparent',
    cursor:'pointer', font:'inherit',
    display:'block',
    overflow:'hidden',
  },
  posterImg: { width:'100%', height:'100%', objectFit:'cover', objectPosition:'center 28%', display:'block', transition:'transform 400ms ease' },
  posterFallback: {
    width:'100%', height:'100%',
    display:'flex', alignItems:'center', justifyContent:'center',
  },
  posterInitials: {
    fontFamily:'var(--font-display)',
    fontSize:88,
    color:'rgba(255,255,255,.92)',
    letterSpacing:'-.02em',
    lineHeight:1,
  },
  posterScrim: {
    position:'absolute', left:0, right:0, bottom:0, height:'60%',
    background:'linear-gradient(to top, rgba(0,0,0,.92) 0%, rgba(0,0,0,.7) 35%, rgba(0,0,0,0) 100%)',
    pointerEvents:'none',
  },
  overlay: {
    position:'absolute', left:18, right:18, bottom:16,
    color:'#fff', textAlign:'left',
    pointerEvents:'none',
  },
  overlayName: {
    fontFamily:'var(--font-display)',
    fontSize:22, lineHeight:1.15, letterSpacing:'-.01em',
    margin:'0 0 4px',
    textWrap:'balance',
  },
  overlayRole: {
    fontSize:12.5, lineHeight:1.35,
    color:'rgba(255,255,255,.82)',
    fontWeight:500,
  },
  play: {
    position:'absolute', top:14, right:14,
    width:38, height:38, borderRadius:'50%',
    background:'rgba(0,0,0,.55)', backdropFilter:'blur(6px)',
    display:'flex', alignItems:'center', justifyContent:'center', paddingLeft:3,
    border:'1px solid rgba(255,255,255,.25)',
    transition:'background 180ms ease',
  },
  video: {
    position:'absolute', inset:0,
    width:'100%', height:'100%',
    background:'#000',
    objectFit:'cover',
  },
  closeBtn: {
    position:'absolute', top:10, right:10,
    width:32, height:32, borderRadius:'50%',
    background:'rgba(0,0,0,.65)',
    color:'#fff',
    border:'1px solid rgba(255,255,255,.2)',
    cursor:'pointer',
    display:'flex', alignItems:'center', justifyContent:'center',
    zIndex:2,
  },
};

const ccFeatStyles = {
  wrap: { background:'#fff', padding:'24px 32px 64px', borderTop:'1px solid var(--border)' },
  inner: { maxWidth:1320, margin:'0 auto' },
  grid: {
    display:'grid',
    gridTemplateColumns:'minmax(0, 1.55fr) minmax(0, 1fr)',
    gap:48,
    alignItems:'center',
    paddingTop:56,
  },
  playerCol: { minWidth:0 },
  frame: {
    position:'relative',
    aspectRatio:'16/9',
    borderRadius:16,
    overflow:'hidden',
    background:'#0E0A0A',
    boxShadow:'0 24px 60px -24px rgba(73,3,0,.45), 0 4px 12px -4px rgba(73,3,0,.18)',
  },
  originTag: {
    position:'absolute', top:16, left:16, zIndex:3,
    background:'var(--brandRed)', color:'#fff',
    fontSize:11, fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase',
    padding:'6px 12px', borderRadius:6,
    boxShadow:'0 2px 8px -2px rgba(0,0,0,.4)',
  },
  posterWrap: { position:'absolute', inset:0 },
  posterBg: {
    position:'absolute', inset:0,
    background:'linear-gradient(135deg, #1C1414 0%, #0E0A0A 70%)',
  },
  posterStripes: {
    position:'absolute', inset:0,
    backgroundImage:'repeating-linear-gradient(45deg, rgba(255,255,255,.035) 0, rgba(255,255,255,.035) 2px, transparent 2px, transparent 16px)',
  },
  posterScrim: {
    position:'absolute', left:0, right:0, bottom:0, height:'62%',
    background:'linear-gradient(to top, rgba(0,0,0,.88) 0%, rgba(0,0,0,.5) 40%, rgba(0,0,0,0) 100%)',
  },
  posterMeta: {
    position:'absolute', left:24, bottom:22, textAlign:'left', color:'#fff',
    pointerEvents:'none',
  },
  posterName: {
    fontFamily:'var(--font-display)', fontSize:34, lineHeight:1.05, letterSpacing:'-.01em', margin:'0 0 4px',
  },
  posterRole: { fontSize:13, letterSpacing:'.04em', color:'rgba(255,255,255,.82)', fontWeight:500 },
  qualityCentre: {
    position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)',
    display:'flex', flexDirection:'column', alignItems:'center', gap:14,
    width:'min(100%, 480px)', padding:'0 16px',
  },
  qualityRow: {
    display:'flex', gap:14, flexWrap:'wrap', justifyContent:'center',
  },
  qualityBtnPrimary: {
    appearance:'none', cursor:'pointer',
    display:'inline-flex', alignItems:'center', gap:9,
    padding:'13px 22px 13px 18px', borderRadius:999,
    background:'rgba(219,30,23,.95)', color:'#fff',
    border:'1.5px solid rgba(255,255,255,.42)',
    fontFamily:'inherit', fontSize:14.5, fontWeight:600, letterSpacing:'.01em',
    boxShadow:'0 8px 24px -6px rgba(0,0,0,.5)',
    backdropFilter:'blur(4px)',
  },
  qualityBtnGhost: {
    appearance:'none', cursor:'pointer',
    display:'inline-flex', alignItems:'center', gap:9,
    padding:'13px 22px 13px 18px', borderRadius:999,
    background:'rgba(0,0,0,.55)', color:'#fff',
    border:'1.5px solid rgba(255,255,255,.32)',
    fontFamily:'inherit', fontSize:14.5, fontWeight:600, letterSpacing:'.01em',
    boxShadow:'0 8px 24px -6px rgba(0,0,0,.5)',
    backdropFilter:'blur(4px)',
  },
  qualityHint: {
    fontSize:12, letterSpacing:'.02em',
    color:'rgba(255,255,255,.72)',
    textAlign:'center',
    pointerEvents:'none',
  },
  video: { position:'absolute', inset:0, width:'100%', height:'100%', background:'#000', objectFit:'contain' },
  errorState: {
    position:'absolute', inset:0, display:'flex', flexDirection:'column',
    alignItems:'center', justifyContent:'center', gap:6, textAlign:'center', padding:24,
    background:'linear-gradient(135deg, #1C1414 0%, #0E0A0A 70%)',
  },
  errorTitle: { fontFamily:'var(--font-display)', fontSize:22, color:'#fff' },
  errorBody: { fontSize:13.5, color:'rgba(255,255,255,.7)' },
  textCol: { minWidth:0 },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:600, marginBottom:16 },
  h2: { fontFamily:'var(--font-display)', fontSize:34, lineHeight:1.1, letterSpacing:'-.015em', margin:'0 0 18px', textWrap:'balance' },
  body: { fontSize:16.5, lineHeight:1.62, color:'var(--grey)', margin:'0 0 26px', textWrap:'pretty' },
  chips: { display:'flex', flexWrap:'wrap', gap:10 },
  chip: {
    fontSize:12.5, fontWeight:600, letterSpacing:'.02em', whiteSpace:'nowrap',
    color:'var(--brandRed)', background:'var(--pastelRed)',
    padding:'7px 14px', borderRadius:999,
  },
};

const ccOutroStyles = {
  wrap: { background:'#fff', padding:'80px 32px 96px', borderTop:'1px solid var(--border)' },
  inner: { maxWidth:680, margin:'0 auto', textAlign:'center' },
  h2: { fontFamily:'var(--font-display)', fontSize:36, lineHeight:1.1, letterSpacing:'-.015em', margin:'0 0 16px', textWrap:'balance' },
  body: { fontSize:17, lineHeight:1.6, color:'var(--grey)', margin:'0 0 32px', textWrap:'pretty' },
  ctaRow: { display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' },
  ctaPrimary: {
    appearance:'none',
    background:'var(--brandRed)', color:'#FDF5F5',
    border:'1px solid var(--brandRed)',
    padding:'14px 28px', borderRadius:10,
    fontFamily:'inherit', fontSize:15, fontWeight:600,
    textDecoration:'none',
    cursor:'pointer',
    display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8,
  },
  ctaGhost: {
    appearance:'none',
    background:'transparent', color:'var(--brandRed)',
    border:'1px solid var(--border-strong)',
    padding:'14px 28px', borderRadius:10,
    fontFamily:'inherit', fontSize:15, fontWeight:600,
    textDecoration:'none',
    cursor:'pointer',
    display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8,
  },
};
